import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, Bell, MessageCircle, LogOut, User, Wallet, Shield, Heart, FileText, Sun, Moon, Monitor, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/LanguageSelector";
import { CurrencySelector } from "@/components/CurrencySelector";
import { BrandMark } from "@/components/BrandLogo";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useTheme } from "@/hooks/use-theme";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      
      setIsAdmin(!!data);
    };
    
    checkAdminRole();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const userInitials = user?.user_metadata?.full_name
    ?.split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase() || user?.email?.[0]?.toUpperCase() || "U";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <BrandMark iconClassName="text-white" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 md:flex">
          <Link
            to="/browse"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.browse}
          </Link>
          <Link
            to="/how-it-works"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.howItWorks}
          </Link>
          <Link
            to="/for-professionals"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.forProfessionals}
          </Link>
        </nav>

        {/* Desktop Actions */}
        <div className="hidden items-center gap-3 md:flex">
          <Button variant="ghost" size="icon" className="relative" aria-label="Search" onClick={() => navigate("/browse")}>
            <Search className="h-5 w-5" />
          </Button>
          
          {user ? (
            <>
              <Link to="/chat">
                <Button variant="ghost" size="icon" className="relative" aria-label="Messages">
                  <MessageCircle className="h-5 w-5" />
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    3
                  </span>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" className="relative" aria-label="Notifications" onClick={() => navigate("/notifications")}>
                <Bell className="h-5 w-5" />
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                  5
                </span>
              </Button>
            </>
          ) : null}
          
          <LanguageSelector />
          
          <div className="h-6 w-px bg-border" />
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" aria-label="Open user menu">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="font-medium">{user?.user_metadata?.full_name || "User"}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  {t.nav.profile}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wallet")}>
                  <Wallet className="mr-2 h-4 w-4" />
                  {t.nav.wallet}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/bookmarks")}>
                  <Heart className="mr-2 h-4 w-4" />
                  {t.nav.bookmarks}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/invoices")}>
                  <FileText className="mr-2 h-4 w-4" />
                  {t.invoice.title}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/jobs")}>
                  <Megaphone className="mr-2 h-4 w-4" />
                  My broadcasts
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/chat")}>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  {t.nav.messages}
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="mr-2 h-4 w-4" />
                      {t.nav.admin}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin/moderation")}>
                      <Shield className="mr-2 h-4 w-4" />
                      Moderation
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                  {theme === "dark" ? (
                    <Sun className="mr-2 h-4 w-4" />
                  ) : (
                    <Moon className="mr-2 h-4 w-4" />
                  )}
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </DropdownMenuItem>
                <div
                  className="px-2 py-1.5"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <p className="mb-1 text-xs text-muted-foreground">Display currency</p>
                  <CurrencySelector className="w-full" />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="ghost" size="sm">
                  {t.nav.login}
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button size="sm">{t.nav.signup}</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-2 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="border-t border-border bg-card md:hidden max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="container flex flex-col gap-1 px-4 py-4 pb-24">
            <Link
              to="/jobs/new"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Hire multiple providers
            </Link>
            {user && (
              <>
                <Link
                  to="/bookmarks"
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t.nav.bookmarks}
                </Link>
                <Link
                  to="/chat"
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t.nav.messages}
                </Link>
                <Link
                  to="/wallet"
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t.nav.wallet}
                </Link>
                <Link
                  to="/profile"
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t.nav.profile}
                </Link>
              </>
            )}

            <div className="my-2 h-px bg-border" />

            {/* Company links */}
            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Display currency
            </p>
            <div className="px-3 pb-2">
              <CurrencySelector className="w-full" />
              <p className="mt-1 text-xs text-muted-foreground">
                Prices are shown in each service's own currency, with an approximate value here.
              </p>
            </div>

            <div className="my-2 h-px bg-border" />

            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t.footer.company}
            </p>
            <Link
              to="/about"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.about}
            </Link>
            <Link
              to="/how-it-works"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.howItWorks}
            </Link>
            <Link
              to="/for-professionals"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.forProfessionals}
            </Link>
            <Link
              to="/pricing"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.pricing}
            </Link>
            <Link
              to="/contact"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.contactUs}
            </Link>

            <div className="my-2 h-px bg-border" />

            {/* Support links */}
            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t.footer.support}
            </p>
            <Link
              to="/help"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.helpCenter}
            </Link>
            <Link
              to="/safety"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.safetyGuidelines}
            </Link>
            <Link
              to="/trust"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.trustVerification}
            </Link>

            <div className="my-2 h-px bg-border" />

            {/* Legal links */}
            <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t.footer.legal}
            </p>
            <Link
              to="/terms"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.termsConditions}
            </Link>
            <Link
              to="/privacy"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.privacyPolicy}
            </Link>
            <Link
              to="/cookies"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.cookiePolicy}
            </Link>
            <Link
              to="/refund"
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.footer.refundPolicy}
            </Link>

            <div className="my-2 h-px bg-border" />
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                setTheme(theme === "dark" ? "light" : "dark");
                setIsMobileMenuOpen(false);
              }}
            >
              {theme === "dark" ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </Button>
            <div className="flex gap-2 pt-2">
              {user ? (
                <Button variant="outline" className="w-full" onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t.nav.logout}
                </Button>
              ) : (
                <>
                  <Link to="/auth" className="flex-1">
                    <Button variant="outline" className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                      {t.nav.login}
                    </Button>
                  </Link>
                  <Link to="/auth?mode=signup" className="flex-1">
                    <Button className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                      {t.nav.signup}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
